import Tile from 'ol/layer/Tile'
import TileImage from 'ol/source/TileImage'
import TileGrid from 'ol/tilegrid/TileGrid'
import VectorTile from 'ol/source/VectorTile'
import MVT from 'ol/format/MVT'
import VectorTileLayer from 'ol/layer/VectorTile'
import { buffer } from 'ol/extent'
import { GeoJSON } from 'ol/format'
import Pbf from 'pbf'
import geobuf from 'geobuf'
import { get } from '../binaryajax'

import { offlineVectorSource, isOffline } from './offline'

const origin = [218128, 6126002]
const resolutions = [
  4096,
  2048,
  1024,
  512,
  256,
  128,
  64,
  32,
  16,
  8,
  4,
  2,
  1,
  0.5
]

export const styleMap = { }

export function attach(map, layer, offlinePackage) {
  if (
    layer &&
    layer.metadata &&
    (layer.metadata.offline || layer.metadata.offlineBackground)
  ) {
    attachSync(map, layer, offlinePackage)
  }
}

function attachSync(map, layer, offlinePackage, background) {
  if (layer.offlineLayer_ && !background)
    map.getLayers().remove(layer.offlineLayer_)
  const name =
    layer.metadata.offline && layer.metadata.offline.name
      ? layer.metadata.offline.name
      : layer.source.params.LAYERS
  let { imageLayer, vectorLayer } = findByName(offlinePackage, name)
  if (imageLayer || vectorLayer) {
    console.log('Attaching offline layer ' + layer.name)
    if (imageLayer)
      layer.offlineLayer_ = createLayer(layer, offlinePackage, imageLayer)
    if (vectorLayer)
      layer.offlineLayer_ = createVectorTileLayer(
        layer,
        offlinePackage,
        vectorLayer
      )
    const layers = map.getLayers()
    const index = layers.getArray().indexOf(layer.mapLayer_)
    layers.insertAt(index, layer.offlineLayer_)
  }
  if (vectorLayer) {
    attachSyncGbf(layer, offlinePackage, vectorLayer)
  }
}

export function attachSyncGbf(layer, offlinePackage, vectorLayer) {
  const { path, id } = offlinePackage
  const name = vectorLayer.name.replace(':', '.')
  get(`${path}/${id}/${name}.geobuf`, (err, data) => load(layer, err, data))
}

function findByName(offlinePackage, name) {
  return {
    imageLayer: offlinePackage.imageLayers.filter(x => x.name === name)[0],
    vectorLayer: offlinePackage.vectorLayers.filter(x => x.name === name)[0]
  }
}

export function createLayer(layer, offlinePackage, config) {
  const { path, id } = offlinePackage
  const name = config.name.replace(':', '_')
  const extent = offlinePackage.bbox.split(',').map(c => parseFloat(c))
  const ext = config.mimeType.split('/')[1]
  const normalizedExt = ext === 'png8' ? 'png' : ext

  return new Tile({
    extent: extent,
    // minResolution: resolutions[config.zoomStop],
    maxResolution: resolutions[config.zoomStart - 1],
    visible: isOffline && layer.visible,
    cacheSize: 128,

    source: new TileImage({
      projection: 'EPSG:3006',
      tileUrlFunction: c =>
        `${path}/${id}/${name}/${c[0]}/${c[1]}/${-c[2] - 1}.${normalizedExt}`,
      tileGrid: new TileGrid({
        origin: origin,
        resolutions: resolutions.slice(0, config.zoomStop + 1)
      })
    })
  })
}

function loadTileData(url) {
  return new Promise((resolve, reject) => {
    get(url, (err, data) => {
      if (err) {
        reject(err)
      } else {
        resolve(data)
      }
    })
  })
}

function tileLoadFunction(tile, url) {
  tile.setLoader(function(extent, _, projection) {
    loadTileData(url)
      .then(function(data) {
        const format = tile.getFormat()
        const features = format.readFeatures(data, {
          extent: extent,
          featureProjection: projection
        })
        tile.setFeatures(features)
      })
      .catch(error => {
        console.log(error, url)
        tile.setFeatures([])
      })
  })
}

export function createVectorTileLayer(
  layer,
  offlinePackage,
  config,
  resolutions
) {
  const name = config.name.replace(':', '_')
  const extent = offlinePackage.bbox.split(',').map(c => parseFloat(c))
  const style = styleMap[config.name]
  const { path, id } = offlinePackage

  const availableTiles = {}
  if (config.availableTiles)
    config.availableTiles.forEach(t => (availableTiles[t] = true))

  function tileUrlFunction(c) {
    if (!availableTiles[`${c[0]}/${c[1]}/${-c[2] - 1}`]) return
    return `${path}/${id}/${name}/${c[0]}/${c[1]}/${-c[2] - 1}.pbf`
  }

  const tileGrid = new TileGrid({
    origin: [218128, 6126002],
    resolutions: resolutions || [4096, 2048, 1024, 512, 256, 128, 64, 32, 16, 8]
  })

  const minZoom = config.zoomStart
  const maxZoom = config.zoomStop

  const source = new VectorTile({
    format: new MVT(),
    projection: 'EPSG:3006',
    tileGrid: tileGrid,
    cacheSize: 32,
    maxZoom,
    minZoom,
    overlaps: true,
    tileUrlFunction,
    tileLoadFunction
  })

  return new VectorTileLayer({
    visible: isOffline && layer.visible,
    source: source,
    extent: buffer(extent, 5000),
    minZoom,
    maxZoom,
    renderBuffer: 256,
    renderMode: 'vector',
    declutter: false,
    style
  })
}

export function gbf2features(data) {
  const geojson = geobuf.decode(new Pbf(data))
  const format = new GeoJSON()
  const features = format.readFeatures(geojson)
  return features
}

function load(layer, err, data) {
  if (err) return
  const name = layer.name
  const metadata = layer.metadata
  const features = gbf2features(data)
  features.forEach(f => f.set('olga_', { name, layer, metadata }))
  offlineVectorSource.addFeatures(features)
}
